import React, { useCallback, useState } from 'react'
import { QuantumThemeWrapper } from '@sendoutcards/quantum-design-ui'
import { performOperation } from 'src/app/api'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import {
  CreateSparseUser,
  CreateSparseUserData,
  InviteFragment,
  SponsorFragment,
} from 'src/graphql'
import { openApp } from 'src/helpers/appHelpers'
import useSelector from 'src/hooks/useSelector'
import { GreetingCardCarousel } from 'src/marketing/components/GreetingCardCarousel'
import { LaunchButton } from 'src/marketing/components/LaunchButton'
import {
  LaunchFeatureDialog,
  LaunchFeatureDialogKey,
} from 'src/marketing/components/LaunchFeatureDialog'
import { LaunchSection } from 'src/marketing/components/LaunchSection'
import {
  LaunchAction,
  launchCatalogCards,
  LaunchSection as LaunchSectionType,
  launchSections,
} from './launchPageContent'
import styles from './launchPage.module.scss'

type LaunchPageType = { invite?: InviteFragment }

const getSponsorSuffix = (sponsor: SponsorFragment | undefined): string => {
  if (!sponsor) return ''
  return sponsor.genealogyId
    ? `${sponsor.genealogyId}`
    : sponsor.storefront && sponsor.storefront.slug
    ? `${sponsor.storefront.slug}`
    : ''
}

const LaunchPage: React.FC<LaunchPageType> = ({ invite }) => {
  const storefrontUser = useSelector(state => state.storefront.storefront?.user)
  const [activeDialog, setActiveDialog] = useState<
    LaunchFeatureDialogKey | undefined
  >()

  const goToApp = useCallback((path = '', query?: Record<string, string>) => {
    openApp(path, query)
  }, [])

  const scrollToSection = useCallback((targetId: string) => {
    document
      .getElementById(targetId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const openPromptings = useCallback(() => {
    window.location.href = `https://www.promptings.com/?sponsor=${getSponsorSuffix(
      storefrontUser,
    )}`
  }, [storefrontUser])

  const openPricing = useCallback(() => {
    window.location.href = `https://app.sendoutcards.com/pricing${
      storefrontUser ? '?sponsor=' + getSponsorSuffix(storefrontUser) : ''
    }`
  }, [storefrontUser])

  const handleSendFreeCard = useCallback(() => {
    if (invite) {
      performOperation<CreateSparseUserData>(
        CreateSparseUser({ inviteId: invite.id }),
        result => {
          if (result.value) {
            goToApp('', {
              access_token: result.value.createSparseUser.account.token,
              postcardPromo: '',
            })
            return
          }

          goToApp('', {
            verify: '',
            postcardPromo: '',
          })
        },
      )
      return
    }

    goToApp('', {
      verify: '',
      postcardPromo: '',
    })
  }, [goToApp, invite])

  const resolveAction = useCallback(
    (action?: LaunchAction) => {
      if (!action) return undefined

      return () => {
        switch (action.type) {
          case 'send_free_card':
            handleSendFreeCard()
            return
          case 'pricing':
            goToApp('pricing')
            return
          case 'scroll_to': {
            scrollToSection(action.targetId)
            return
          }
          case 'external_url':
            if (action.url) {
              window.location.href = action.url
            }
            return
          default:
            return
        }
      }
    },
    [goToApp, handleSendFreeCard, scrollToSection],
  )

  const handleOpenDialog = useCallback((modalKey: LaunchFeatureDialogKey) => {
    setActiveDialog(modalKey)
  }, [])

  const renderSection = (section: LaunchSectionType) => (
    <LaunchSection
      key={section.id}
      section={section}
      onPrimaryAction={resolveAction(section.primaryAction)}
      onSecondaryAction={resolveAction(section.secondaryAction)}
      onScrollPoint={scrollToSection}
      onInfoPoint={handleOpenDialog}
    />
  )

  return (
    <QuantumThemeWrapper>
      <main className={styles.page}>
        <header className={styles.header}>
          <div className={`${styles.shell} ${styles.headerRow}`}>
            <button
              className={styles.brand}
              type="button"
              onClick={() => scrollToSection('hero')}
              aria-label="Promptings home"
            >
              <Icon name="promptings_powered_by_soc" />
            </button>

            <nav
              className={styles.navLinks}
              aria-label="Launch page navigation"
            >
              <button type="button" onClick={openPromptings}>
                Promptings
              </button>
              <button type="button" onClick={openPricing}>
                Pricing
              </button>
            </nav>

            <div className={styles.headerActions}>
              <button
                className={styles.loginButton}
                type="button"
                onClick={() => goToApp()}
              >
                Login
              </button>
              <LaunchButton
                label="Send a Free Card"
                variant="primary"
                onClick={handleSendFreeCard}
              />
            </div>
          </div>
        </header>
        <div className={styles.content}>
          {renderSection(launchSections.hero)}

          <section
            id="cards-ribbon"
            className={styles.ribbonSection}
            aria-labelledby="cards-ribbon-heading"
          >
            <div className={styles.shell}>
              <div className={styles.ribbonIntro}>
                <p className={styles.eyebrow}>Our Catalog</p>
                <h2 id="cards-ribbon-heading">
                  Thousands of world-class designs, ready for the moment you
                  feel it.
                </h2>
                <p>
                  Browse an industry-leading catalog with thousands of
                  world-class designs crafted by our professional designers for
                  birthdays, gratitude, business follow-up, encouragement,
                  celebrations, and everything in between.
                </p>
              </div>
              <GreetingCardCarousel
                cards={launchCatalogCards}
                cardWidth={132}
                cardHeight={185}
                gap={16}
              />
            </div>
          </section>

          {renderSection(launchSections.actOnPromptings)}
          {renderSection(launchSections.digitalAndPrinted)}
          {renderSection(launchSections.crm)}
          {renderSection(launchSections.customization)}
          {renderSection(launchSections.gifts)}
        </div>
        <div className={styles.backToTopSection}>
          <button
            className={styles.backToTopButton}
            type="button"
            onClick={() => scrollToSection('hero')}
            aria-label="Back to top"
          >
            <span>Back to top</span>
            <span className={styles.backToTopChevron} aria-hidden="true" />
          </button>
        </div>
        <LaunchFeatureDialog
          activeDialog={activeDialog}
          onClose={() => setActiveDialog(undefined)}
        />
      </main>
    </QuantumThemeWrapper>
  )
}

export default LaunchPage
